import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { DefaultEditableActionItemContentCSS, IImpersonationContextComponentProps } from '../../../../../models';
import { isActionItemStep } from '../../../../../models/Automations';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../models/UiUtils';
import { useUpdateAutomationStepMutation } from '../../../../../queries/Automation/mutations/steps';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { titles } from '../../../../styles/colors';
import { Collapsible } from '../../../Collapsible';
import { InputFieldError } from '../../../InputFieldError';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../richContent/RichContentDocumentEditor';
import { AdvancedOptionsButton } from '../presentation';
import { styleSheet } from './../styles';
import { ContactFilterOption } from './ContactFilterOption';
import { StepSendFromOption } from './StepSendFromOption';

const DefaultActionItemEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS.replace(/color:[ ]?[#]?[a-z0-9]*;/i, `color: ${titles};`),
	minHeight: 14,
	plugins: [],
	toolbar: false,
};

interface ActionItemStepConfigProps extends IImpersonationContextComponentProps, IConnectionTypeComponentProps {
	step: Api.IAutomationStep;
	canEdit: boolean;
	templateId: string;
	errorMessage: string | null;
	onClearErrorMessages: () => void;
	onStepChanged?: () => void;
}

export function ActionItemStepConfig({
	step,
	canEdit,
	templateId,
	errorMessage,
	onClearErrorMessages,
	onStepChanged,
	impersonationContext,
	connectionTypes,
}: ActionItemStepConfigProps) {
	const [actionItemContentEditorState, setActionItemContentEditorState] =
		React.useState<Api.IRichContentEditorState | null>(() => {
			if (isActionItemStep(step)) {
				return convertRawRichTextContentStateToRichContentEditorState(step.content);
			}
			return null;
		});
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState(step?.contactFilter);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		return !!skipStepContactFilter?.criteria || !!step?.sendFromOptions;
	});

	const updateStepMutation = useUpdateAutomationStepMutation({
		onSuccess: () => onStepChanged?.(),
	});

	const save = ({
		newContactFilter = skipStepContactFilter,
		newSendFrom,
	}: {
		newContactFilter?: Api.IContactFilterCriteria;
		newSendFrom?: Api.ISendFromOptions | null;
	} = {}) => {
		// Spread existing model to preserve all fields (schedule, name, etc.) that aren't being changed
		const existingModel = step as Api.IActionItemAutomationStep;

		const stepModel: Api.IActionItemAutomationStep = {
			...existingModel,
			content: actionItemContentEditorState?.getRawRichTextContent(),
			contactFilter: newContactFilter,
			// Preserve sendFromOptions unless explicitly updated (newSendFrom can be null to clear it)
			sendFromOptions: newSendFrom !== undefined ? newSendFrom : existingModel?.sendFromOptions,
		};

		updateStepMutation.mutate({
			templateId,
			stepId: step.id,
			stepType: Api.AutomationStepType.ActionItem,
			step: stepModel,
			impersonationContext,
		});
	};

	const onActionItemContentEditorStateChanged = (newActionItemContentEditorState: Api.IRichContentEditorState) => {
		setActionItemContentEditorState(newActionItemContentEditorState);
		onClearErrorMessages();
	};

	const saveSenderOptions = (option: Api.ISendFromOptions) => {
		save({ newSendFrom: option });
	};

	const baseKey = `${step?._type}-${step?.id || ''}`;

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>Type action item below:</div>
			<InputFieldError errorMessage={errorMessage}>
				<RichContentDocumentEditor
					config={DefaultActionItemEditorConfig}
					contentPlaceholderText='Type action item...'
					contentState={actionItemContentEditorState}
					key={`${baseKey}-action-item-content`}
					onBlur={() => save()}
					onContentStateChanged={onActionItemContentEditorStateChanged}
					onFocus={onClearErrorMessages}
					readOnly={!canEdit}
					styles={[styleSheet.actionItemContentEditor]}
				/>
			</InputFieldError>
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<>
						<StepSendFromOption
							step={step}
							onSave={saveSenderOptions}
							connectionTypes={connectionTypes}
							impersonationContext={impersonationContext}
							sendFromLabel={'Always assign this action item to'}
						/>
						<ContactFilterOption
							canEdit={canEdit}
							impersonationContext={impersonationContext}
							skipStepContactFilter={skipStepContactFilter}
							stepUuid={step.id}
							stepIsBusy={false}
							onFilterChange={filter => {
								setSkipStepContactFilter(filter);
								save({ newContactFilter: filter });
							}}
						/>
					</>
				</div>
			</Collapsible>
		</>
	);
}
